import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { extractErrorMessage } from 'src/app/service/admin-error';

/** captureSurfacedFailure 에 주입하는 Sentry 보고면. 스펙에서 레벨 라우팅을 고정한다. */
export type SentryReporter = Pick<typeof Sentry, 'captureMessage' | 'captureException'>;

const TRANSIENT_ERROR_NAMES = new Set(['TimeoutError', 'SwallowedHttpError']);

/**
 * 메시지에 남아 message-only 판정을 내리는 표식. '502' 나 홑단어 매칭은
 * 티켓 번호·검색어 같은 일반 텍스트까지 삼키므로 쓰지 않는다.
 */
const SURFACED_MESSAGE_MARKERS = [/Http failure response/, /Failed to fetch/, /HTTP [45]\d{2}/, /502 가 .*삼켜졌/];

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? '');
}

function causeOf(error: unknown): unknown {
  return (error as { cause?: unknown })?.cause;
}

/** cause 사슬에서 첫 HttpErrorResponse 를 찾는다. 없으면 undefined. */
function findHttpError(error: unknown): HttpErrorResponse | undefined {
  let current: unknown = error;
  for (let depth = 0; depth < 5 && current != null; depth++) {
    if (current instanceof HttpErrorResponse) return current;
    const next = causeOf(current);
    if (next == null || next === current) break;
    current = next;
  }
  return undefined;
}

/**
 * 사용자에게 이미 알린 HTTP·네트워크 실패인지. 이런 것은 프론트 버그가 아니라
 * 백엔드·망 일시 장애이므로 Sentry error 가 아니라 warning + 메시지로만 남긴다.
 * 감싼 에러(photoUploadError 이중 래핑 등)는 cause 사슬을 따라 내려가며 본다.
 */
export function isSurfacedHttpFailure(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < 5 && current != null; depth++) {
    if (current instanceof HttpErrorResponse) return true;
    if (current instanceof Error && TRANSIENT_ERROR_NAMES.has(current.name)) return true;
    if (SURFACED_MESSAGE_MARKERS.some((marker) => marker.test(messageOf(current)))) return true;
    const next = causeOf(current);
    if (next == null || next === current) break;
    current = next;
  }
  return false;
}

/**
 * 사용자에게 알린 실패를 보고한다. HTTP·네트워크면 warning 메시지로만,
 * 그 외(TypeError 등 코드 버그)는 error 로 스택과 함께 올린다.
 * HttpErrorResponse 는 Error 를 상속하지 않아 스택이 없으므로 사슬에서
 * 찾아낸 첫 HTTP 실패의 status·url 을 httpStatus·httpUrl 로 덧붙인다.
 * 호출부 extra 의 url(화면 경로)과 키를 달리해 덮어쓰지 않는다.
 * 403 은 확정된 인가 거부라 info 로만 남긴다. 401 은 토큰 상태를 여기서
 * 알 수 없어 warning 유지 — 아는 호출부(role-guard)만 info 로 보낸다.
 */
export function captureSurfacedFailure(
  error: unknown,
  options: { feature: string; phase: string; extra?: Record<string, unknown> },
  reporter: SentryReporter = Sentry,
): void {
  const { feature, phase, extra } = options;
  const tags = { feature, phase };
  if (!isSurfacedHttpFailure(error)) {
    reporter.captureException(error, { tags, extra });
    return;
  }
  const http = findHttpError(error);
  const httpExtra = http ? { httpStatus: http.status, httpUrl: http.url ?? undefined } : undefined;
  reporter.captureMessage(extractErrorMessage(error, String(error)), {
    level: http?.status === 403 ? 'info' : 'warning',
    tags,
    extra: { ...httpExtra, ...extra },
  });
}

/**
 * 확정된 인증·인가 결과인지(401-만료·403-거부). 이런 조회 거부는 info 로만
 * 남기고, 살아 있는 토큰의 401(서버가 유효 세션을 거부 — 장애 가능)은
 * warning 경로(captureSurfacedFailure)로 넘긴다.
 */
export function isExpectedAuthRejection(status: number | undefined, hasLiveToken: boolean): boolean {
  if (status === 403) return true;
  return status === 401 && !hasLiveToken;
}
